import React, { useState } from 'react';
import { ArrowRight, Check, RotateCcw, HelpCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
type Step = {
  id: number;
  question: string;
  options: {
    label: string;
    value: string;
  }[];
};
const steps: Step[] = [{
  id: 1,
  question: "What's your project status?",
  options: [{
    label: 'New Construction',
    value: 'new'
  }, {
    label: 'Replacing Old Driveway',
    value: 'replace'
  }, {
    label: 'Retrofitting Existing Surface',
    value: 'retrofit'
  }]
}, {
  id: 2,
  question: 'Approximate size of the heated area?',
  options: [{
    label: 'Small (Under 400 sq ft)',
    value: 'small'
  }, {
    label: 'Medium (400 - 800 sq ft)',
    value: 'medium'
  }, {
    label: 'Large (800 - 1500 sq ft)',
    value: 'large'
  }, {
    label: 'Massive (1500+ sq ft)',
    value: 'massive'
  }]
}, {
  id: 3,
  question: 'What surface type are you planning?',
  options: [{
    label: 'Concrete',
    value: 'concrete'
  }, {
    label: 'Asphalt',
    value: 'asphalt'
  }, {
    label: 'Pavers / Stone',
    value: 'pavers'
  }, {
    label: 'Not sure yet',
    value: 'unsure'
  }]
}, {
  id: 4,
  question: 'What matters most to you?',
  options: [{
    label: 'Lowest Upfront Cost',
    value: 'upfront'
  }, {
    label: 'Lowest Monthly Bill',
    value: 'operating'
  }, {
    label: 'Simplest Maintenance',
    value: 'maintenance'
  }]
}];
export function DecisionGuideSection() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResult, setShowResult] = useState(false);
  const handleOptionSelect = (value: string) => {
    const newAnswers = {
      ...answers,
      [currentStep + 1]: value
    };
    setAnswers(newAnswers);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResult(true);
    }
  };
  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResult(false);
  };
  const getRecommendation = () => {
    const size = answers[2];
    const priority = answers[4];
    const project = answers[1];
    if (size === 'massive' || size === 'large' && priority === 'operating') {
      return {
        type: 'Hydronic System',
        description: 'For your large area, a hydronic system will offer significant savings on operating costs over time, making the higher upfront investment worth it.',
        fit: 'Best for large scale & lowest operating cost'
      };
    }
    if (project === 'retrofit') {
      return {
        type: 'Electric System (Retrofit)',
        description: "Since you're working with an existing surface, an electric system is likely your only viable option without major excavation. It's thinner and easier to install.",
        fit: 'Best for existing surfaces'
      };
    }
    if (priority === 'maintenance' || priority === 'upfront' || size === 'small') {
      return {
        type: 'Electric System',
        description: 'An electric system is perfect for your needs. It has zero moving parts (maintenance-free), costs less to install, and is highly efficient for small to medium areas.',
        fit: 'Best for reliability & lower install cost'
      };
    }
    return {
      type: 'Custom Assessment Needed',
      description: 'Your needs are unique! You fall right in the middle where either system could work. We recommend a quick site visit to determine the most cost-effective path.',
      fit: 'Requires expert eye'
    };
  };
  const recommendation = showResult ? getRecommendation() : null;
  return <section id="decision-guide" className="py-16 bg-slate-50 md:py-24" data-id="element-993">
      <div className="container mx-auto px-4 md:px-6" data-id="element-994">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-995">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-996">
            Find Your Best Fit in 60 Seconds
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-997">
            Answer 4 quick questions to get a personalized recommendation.
          </p>
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-998">
          <Card className="shadow-xl border-slate-200 overflow-hidden" data-id="element-999">
            <div className="bg-slate-100 h-2 w-full" data-id="element-1000">
              <div className="bg-primary h-full transition-all duration-500 ease-out" style={{
              width: `${(currentStep + 1) / steps.length * 100}%`
            }} data-id="element-1001"></div>
            </div>

            <CardContent className="p-8 md:p-12" data-id="element-1002">
              {!showResult ? <div className="animate-in fade-in slide-in-from-right-4 duration-300" key={currentStep} data-id="element-1003">
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider mb-2 block" data-id="element-1004">
                    Question {currentStep + 1} of {steps.length}
                  </span>
                  <h3 className="text-2xl font-bold text-slate-900 mb-8" data-id="element-1005">
                    {steps[currentStep].question}
                  </h3>

                  <div className="grid gap-4" data-id="element-1006">
                    {steps[currentStep].options.map(option => <button key={option.value} onClick={() => handleOptionSelect(option.value)} className="text-left p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all duration-200 flex items-center justify-between group" data-id="element-1007">
                        <span className="font-medium text-slate-700 group-hover:text-primary" data-id="element-1008">
                          {option.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all" data-id="element-1009" />
                      </button>)}
                  </div>
                </div> : <div className="text-center animate-in fade-in zoom-in-95 duration-500" data-id="element-1010">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1011">
                    {recommendation?.type.includes('Custom') ? <HelpCircle className="h-8 w-8" data-id="element-1012" /> : <Check className="h-8 w-8" data-id="element-1013" />}
                  </div>

                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1014">
                    We Recommend
                  </h3>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-1015">
                    {recommendation?.type}
                  </h2>
                  <div className="inline-block bg-slate-100 px-3 py-1 rounded-full text-sm font-medium text-slate-700 mb-6" data-id="element-1016">
                    {recommendation?.fit}
                  </div>

                  <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-1017">
                    {recommendation?.description}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1018">
                    <Button size="lg" onClick={() => {
                  const element = document.getElementById('quote-form');
                  if (element) element.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} data-id="element-1019">
                      Get a Quote for This System
                    </Button>
                    <Button variant="ghost" onClick={resetQuiz} className="text-slate-500 hover:text-slate-900" data-id="element-1020">
                      <RotateCcw className="h-4 w-4 mr-2" data-id="element-1021" /> Start Over
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}